// The filter/tag vocabulary shared by the chat dock roster and the Messages
// inbox. Pure data and predicates — no JSX, so it can be unit-tested without
// rendering. Presentation lives in FiltersPanel / InlineFilterRail.

import { TRACKING_WINDOW_DAYS, NEW_CLIENT_WINDOW_DAYS } from "../../../hooks/insights/insightDerive";

export const FILTER_IDS = {
  // Message-state filters that already existed; they now live in the same
  // multi-select set as the rest instead of being a separate string.
  UNREAD: "unread",
  TAGGED: "tagged",
  FLAGGED: "flagged", // Messages inbox only
  // Coaching-signal filters, all of which need insight data.
  DIET_STALE: "dietStale",
  WORKOUT_STALE: "workoutStale",
  NO_PLAN: "noPlan",
  TRACKING: "tracking",
  NOT_TRACKING: "notTracking",
  NEW: "new",
};

/**
 * Filters that need `/fetch-coach-client-insights`. Hidden entirely while the
 * endpoint is unavailable, and never matched by a row whose data hasn't landed.
 */
export const INSIGHT_FILTER_IDS = [
  FILTER_IDS.DIET_STALE,
  FILTER_IDS.WORKOUT_STALE,
  FILTER_IDS.NO_PLAN,
  FILTER_IDS.TRACKING,
  FILTER_IDS.NOT_TRACKING,
  FILTER_IDS.NEW,
];

export const ALL_FILTER_IDS = Object.values(FILTER_IDS);

/**
 * Pairs that describe opposite states. Selecting one clears the other, because
 * ANDing them can only ever produce an empty list — an empty list the coach
 * would reasonably read as a bug rather than as a contradiction.
 */
const EXCLUSIVE_GROUPS = [
  [FILTER_IDS.TRACKING, FILTER_IDS.NOT_TRACKING],
  // "No plan" has no age, so it cannot also be "older than N days".
  [FILTER_IDS.NO_PLAN, FILTER_IDS.DIET_STALE, FILTER_IDS.WORKOUT_STALE],
];

/**
 * Ids made unselectable by picking `id`.
 * @param {string} id
 * @returns {string[]}
 */
export function conflictsWith(id) {
  const group = EXCLUSIVE_GROUPS.find((ids) => ids.includes(id));
  if (!group) return [];
  // Within the stale/no-plan group the two stale chips are compatible with each
  // other — only No plan conflicts with them.
  if (group.includes(FILTER_IDS.NO_PLAN)) {
    return id === FILTER_IDS.NO_PLAN
      ? [FILTER_IDS.DIET_STALE, FILTER_IDS.WORKOUT_STALE]
      : [FILTER_IDS.NO_PLAN];
  }
  return group.filter((other) => other !== id);
}

/**
 * @typedef {Object} FilterDef
 * @property {string} id
 * @property {string} label - Static label; use `filterLabel` for the threshold-aware one.
 * @property {boolean} needsInsights
 * @property {(subject: Object, tags: Object) => boolean} matches
 */

/** @type {Record<string, FilterDef>} */
export const FILTER_DEFS = {
  [FILTER_IDS.UNREAD]: {
    id: FILTER_IDS.UNREAD,
    label: "Unread",
    needsInsights: false,
    matches: (subject) => subject.unread > 0,
  },
  [FILTER_IDS.TAGGED]: {
    id: FILTER_IDS.TAGGED,
    label: "Tagged",
    needsInsights: false,
    matches: (subject) => Boolean(subject.mentioned),
  },
  [FILTER_IDS.FLAGGED]: {
    id: FILTER_IDS.FLAGGED,
    label: "Flagged",
    needsInsights: false,
    matches: (subject) => Boolean(subject.flagged),
  },
  [FILTER_IDS.DIET_STALE]: {
    id: FILTER_IDS.DIET_STALE,
    label: "Diet plan",
    needsInsights: true,
    matches: (_subject, tags) => tags.dietStale,
  },
  [FILTER_IDS.WORKOUT_STALE]: {
    id: FILTER_IDS.WORKOUT_STALE,
    label: "Workout plan",
    needsInsights: true,
    matches: (_subject, tags) => tags.workoutStale,
  },
  [FILTER_IDS.NO_PLAN]: {
    id: FILTER_IDS.NO_PLAN,
    label: "No plan",
    needsInsights: true,
    matches: (_subject, tags) => tags.noPlan,
  },
  [FILTER_IDS.TRACKING]: {
    id: FILTER_IDS.TRACKING,
    label: "Tracking",
    needsInsights: true,
    matches: (_subject, tags) => tags.isTracking,
  },
  [FILTER_IDS.NOT_TRACKING]: {
    id: FILTER_IDS.NOT_TRACKING,
    label: "Not tracking",
    needsInsights: true,
    matches: (_subject, tags) => tags.notTracking,
  },
  [FILTER_IDS.NEW]: {
    id: FILTER_IDS.NEW,
    label: "New",
    needsInsights: true,
    matches: (_subject, tags) => tags.isNew,
  },
};

/**
 * Chip label. The two stale chips carry the live threshold so the label, the
 * predicate and the tooltip can never disagree about what ">10d" means.
 *
 * @param {string} id
 * @param {number} thresholdDays
 * @returns {string}
 */
export function filterLabel(id, thresholdDays) {
  const def = FILTER_DEFS[id];
  if (!def) return id;
  if (id === FILTER_IDS.DIET_STALE || id === FILTER_IDS.WORKOUT_STALE) {
    return `${def.label} >${thresholdDays}d`;
  }
  return def.label;
}

/** Windows re-exported so chip/tooltip copy has a single source. */
export { TRACKING_WINDOW_DAYS, NEW_CLIENT_WINDOW_DAYS };
